import React from 'react';
import type { AiModelConfig, AiModelOption, GeneratorKey, ModelCapabilities } from './types';

interface ModelPickerProps {
  models: AiModelOption[];
  modelId: string;
  onModelId: (id: string) => void;
  config: AiModelConfig;
  onConfig: (config: AiModelConfig) => void;
  generatorKey: GeneratorKey;
  disabled?: boolean;
  allowEmpty?: boolean;
  emptyLabel?: string;
}

const ALL_REASONING_LEVELS = ['minimal', 'low', 'medium', 'high', 'xhigh', 'max'] as const;
const LEVEL_LABELS: Record<string, string> = {
  minimal: 'Minimal',
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  xhigh: 'Very High',
  max: 'Maximum',
};

const WEB_SEARCH_GENERATORS: GeneratorKey[] = ['source_of_truth', 'recipe_attributes'];
const IMAGE_GENERATORS: GeneratorKey[] = [
  'visual_concept',
  'visual_concept_regenerate',
  'visual_concept_edit',
];
const TEXT_GENERATORS: GeneratorKey[] = [
  'source_of_truth',
  'jtbd',
  'tension',
  'benefit',
  'recipe_attributes',
  'written_concept',
];

export function capabilitiesFor(model?: AiModelOption | null): ModelCapabilities {
  if (model?.capabilities && typeof model.capabilities === 'object') {
    return model.capabilities;
  }
  return {
    reasoning: {
      supported: Boolean(model?.supports_reasoning_effort || model?.supports_thinking),
      levels: ['low', 'medium', 'high'],
      disable_supported: false,
    },
    web_search: Boolean(model?.supports_web_search),
    image: {
      supported: Boolean(model?.supports_image_generation),
      quality_levels: ['high', 'max'],
    },
  };
}

export function configForModel(
  model: AiModelOption | undefined,
  prev: AiModelConfig,
  generatorKey: GeneratorKey
): AiModelConfig {
  const caps = capabilitiesFor(model);
  const next: AiModelConfig = {};
  const levels = caps.reasoning?.levels || [];
  if (caps.reasoning?.supported && prev.reasoning_level && levels.includes(prev.reasoning_level)) {
    next.reasoning_level = prev.reasoning_level;
  }
  if (WEB_SEARCH_GENERATORS.includes(generatorKey) && caps.web_search) {
    next.web_search = prev.web_search !== false;
  }
  const qualityLevels = caps.image?.quality_levels || [];
  if (
    IMAGE_GENERATORS.includes(generatorKey) &&
    caps.image?.supported &&
    prev.image_quality &&
    qualityLevels.includes(prev.image_quality)
  ) {
    next.image_quality = prev.image_quality;
  }
  return next;
}

export function modelConfigPayload(modelId: string, config: AiModelConfig): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  if (modelId) payload.model_id = modelId;
  if (config.reasoning_level) payload.reasoning_level = config.reasoning_level;
  if (config.web_search !== undefined) payload.web_search = config.web_search;
  if (config.image_quality) payload.image_quality = config.image_quality;
  return payload;
}

const ModelPicker: React.FC<ModelPickerProps> = ({
  models,
  modelId,
  onModelId,
  config,
  onConfig,
  generatorKey,
  disabled,
  allowEmpty,
  emptyLabel,
}) => {
  const selected = models.find((m) => m.model_id === modelId);
  const caps = capabilitiesFor(selected);
  const showReasoning = TEXT_GENERATORS.includes(generatorKey) && Boolean(caps.reasoning?.supported);
  const showWebSearch = WEB_SEARCH_GENERATORS.includes(generatorKey) && Boolean(caps.web_search);
  const showImage = IMAGE_GENERATORS.includes(generatorKey) && Boolean(caps.image?.supported);
  const supportedLevels = caps.reasoning?.levels || [];
  const qualityLevels = caps.image?.quality_levels || [];

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-[12px] font-medium text-[#626262] mb-1">Model</label>
        <select
          value={modelId}
          onChange={(e) => {
            const id = e.target.value;
            const next = models.find((m) => m.model_id === id);
            onModelId(id);
            onConfig(configForModel(next, config, generatorKey));
          }}
          disabled={disabled}
          className="w-full rounded-md border border-[#D1D1D1] px-3 py-2 text-sm disabled:bg-[#F5F5F5]"
        >
          <option value="">{emptyLabel || (allowEmpty ? 'Default pipeline model' : 'Select a model')}</option>
          {models.map((m) => (
            <option key={`${m.provider}-${m.model_id}`} value={m.model_id}>
              {m.label || m.model_id} ({m.provider})
            </option>
          ))}
        </select>
        {models.length === 0 && (
          <p className="mt-1 text-[12px] text-[#8A8A8A]">
            No active models yet. Refresh the catalog, then activate them in Admin → AI Prompts.
          </p>
        )}
      </div>
      {showReasoning && (
        <div>
          <label className="block text-[12px] font-medium text-[#626262] mb-1">Reasoning</label>
          <select
            value={config.reasoning_level || ''}
            onChange={(e) => {
              const value = e.target.value;
              const next: AiModelConfig = { ...config };
              if (value) next.reasoning_level = value;
              else delete next.reasoning_level;
              onConfig(next);
            }}
            disabled={disabled}
            className="w-full rounded-md border border-[#D1D1D1] px-3 py-2 text-sm"
          >
            <option value="">Provider default</option>
            {ALL_REASONING_LEVELS.map((level) => {
              const available = supportedLevels.includes(level);
              const alwaysOn = level === 'minimal' && caps.reasoning?.disable_supported === false && !available;
              return (
                <option key={level} value={level} disabled={!available}>
                  {LEVEL_LABELS[level]}
                  {!available
                    ? alwaysOn
                      ? ' — always on for this model'
                      : ` — unavailable for ${selected?.label || selected?.model_id}`
                    : ''}
                </option>
              );
            })}
          </select>
        </div>
      )}
      {showWebSearch && (
        <label className="flex items-center gap-2 text-sm text-[#4A4A4A]">
          <input
            type="checkbox"
            checked={config.web_search !== false}
            onChange={(e) => onConfig({ ...config, web_search: e.target.checked })}
            disabled={disabled}
          />
          Web search
        </label>
      )}
      {showImage && (
        <div>
          <label className="block text-[12px] font-medium text-[#626262] mb-1">Image quality</label>
          <select
            value={config.image_quality || ''}
            onChange={(e) => {
              const value = e.target.value;
              const next: AiModelConfig = { ...config };
              if (value) next.image_quality = value;
              else delete next.image_quality;
              onConfig(next);
            }}
            disabled={disabled}
            className="w-full rounded-md border border-[#D1D1D1] px-3 py-2 text-sm"
          >
            <option value="">Provider default</option>
            <option value="high" disabled={!qualityLevels.includes('high')}>
              High{!qualityLevels.includes('high') ? ' — unavailable' : ''}
            </option>
            <option value="max" disabled={!qualityLevels.includes('max')}>
              Maximum{!qualityLevels.includes('max') ? ' — unavailable' : ''}
            </option>
          </select>
        </div>
      )}
    </div>
  );
};

export default ModelPicker;
